import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../api/client';

/**
 * Offline fronta pro sklad (kap. 28): když je zařízení offline nebo volání spadne na síti,
 * akce se uloží do localStorage a přehraje se po připojení – sériově, s `Idempotency-Key`,
 * takže dvojí odeslání nezdvojí příjem. Odpověď 4xx při přehrání = konflikt, který zůstane
 * v seznamu k ručnímu vyřešení.
 */

export interface QueuedAction {
  id: string;
  path: string;
  body: unknown;
  /** Idempotency-Key – stejný při každém pokusu */
  key: string;
  label: string;
  createdAt: string;
  error?: string;
}

const STORAGE_KEY = 'isupply.offlineQueue';
const EVENT = 'isupply:queue';
const MAX_ACTIONS = 500;

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readQueue(): QueuedAction[] {
  try {
    const raw = safeStorage()?.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as QueuedAction[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(items: QueuedAction[]): void {
  try {
    safeStorage()?.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* plný nebo blokovaný storage – hlásí volající */
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
}

export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function enqueue(path: string, body: unknown, label: string): QueuedAction {
  const items = readQueue();
  if (items.length >= MAX_ACTIONS)
    throw new ApiError(0, 'QUEUE_FULL', 'Fronta je plná – připoj se k síti');
  const action: QueuedAction = {
    id: newId(),
    path,
    body,
    key: `offline-${newId()}`,
    label,
    createdAt: new Date().toISOString(),
  };
  writeQueue([...items, action]);
  return action;
}

export function removeQueued(id: string): void {
  writeQueue(readQueue().filter((a) => a.id !== id));
}

export function clearQueue(): void {
  writeQueue([]);
}

/**
 * POST, který offline neselže: bez sítě se uloží do fronty a vrátí `fallback` s `queued: true`.
 * Online chyby (4xx/5xx) se propagují jako dřív.
 */
export async function offlinePost<T extends object>(
  path: string,
  body: unknown,
  label: string,
  fallback: T,
): Promise<T & { queued?: boolean; queueId?: string }> {
  if (!isOnline()) {
    const a = enqueue(path, body, label);
    return { ...fallback, queued: true, queueId: a.id };
  }
  try {
    return await api.postWith<T>(path, body, { 'idempotency-key': `online-${newId()}` });
  } catch (err) {
    if (err instanceof ApiError && err.code === 'NETWORK') {
      const a = enqueue(path, body, label);
      return { ...fallback, queued: true, queueId: a.id };
    }
    throw err;
  }
}

let replaying = false;

/** Přehraje frontu v pořadí; při výpadku sítě skončí, konflikty označí a nechá. */
export async function replayQueue(): Promise<{ sent: number; failed: number; pending: number }> {
  const out = { sent: 0, failed: 0, pending: 0 };
  if (replaying) return out;
  replaying = true;
  try {
    for (const a of readQueue()) {
      if (a.error) {
        out.failed += 1;
        continue;
      }
      try {
        await api.postWith(a.path, a.body, { 'idempotency-key': a.key });
        removeQueued(a.id);
        out.sent += 1;
      } catch (err) {
        if (err instanceof ApiError && err.code === 'NETWORK') {
          out.pending += 1;
          break;
        }
        const message = err instanceof Error ? err.message : 'Chyba';
        writeQueue(readQueue().map((x) => (x.id === a.id ? { ...x, error: message } : x)));
        out.failed += 1;
      }
    }
    out.pending += readQueue().filter((x) => !x.error).length - out.pending;
    return out;
  } finally {
    replaying = false;
  }
}

/** Stav sítě a fronty pro UI; po připojení frontu přehraje sám. */
export function useOfflineQueue() {
  const [online, setOnline] = useState(isOnline());
  const [items, setItems] = useState<QueuedAction[]>(readQueue());
  useEffect(() => {
    const onChange = () => setItems(readQueue());
    const onOnline = () => {
      setOnline(true);
      void replayQueue();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onChange);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    if (isOnline() && readQueue().some((a) => !a.error)) void replayQueue();
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onChange);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);
  const replay = useCallback(() => replayQueue(), []);
  return {
    online,
    pending: items.filter((a) => !a.error),
    failed: items.filter((a) => !!a.error),
    replay,
    remove: removeQueued,
    retryFailed: () => {
      writeQueue(readQueue().map(({ error: _e, ...a }) => a));
      return replayQueue();
    },
  };
}
